export abstract class Vraag {

  static deserializeToType(dto: VraagDto): Vraag | undefined {
    if (dto.type === VraagType.TEXT) {
      return TextVraag.deserialize(dto as TextVraagDto);
    } else if (dto.type === VraagType.TEXTAREA) {
      return TextareaVraag.deserialize(dto as TextareaVraagDto);
    } else if (dto.type === VraagType.RADIO) {
      return RadioVraag.deserialize(dto as RadioVraagDto);
    } else if (dto.type === VraagType.VERKEERSLICHT) {
      return VerkeerslichtVraag.deserialize(dto as VerkeerslichtVraagDto);
    } else if (dto.type === VraagType.SELECT) {
      return SelectVraag.deserialize(dto as SelectVraagDto);
    }
    return undefined;
  }

  constructor(private id: string,
              private type: VraagType,
              private required: boolean,
              private icoon?: string,
  ) {
  }

  getId(): string {
    return this.id;
  }

  getType(): VraagType {
    return this.type;
  }

  hasType(type: VraagType): boolean {
    return this.type === type;
  }

  isTextVraag(): boolean {
    return this.hasType(VraagType.TEXT);
  }

  isTextareaVraag(): boolean {
    return this.hasType(VraagType.TEXTAREA);
  }

  isRadioVraag(): boolean {
    return this.hasType(VraagType.RADIO);
  }

  isVerkeerslichtVraag(): boolean {
    return this.hasType(VraagType.VERKEERSLICHT);
  }

  isSelectVraag(): boolean {
    return this.hasType(VraagType.SELECT);
  }

  isRequired(): boolean {
    return this.required;
  }

  getIcoon(): string | undefined {
    return this.icoon;
  }

  hasIcoon(): boolean {
    return !!this.icoon;
  }
}

export class TextVraag extends Vraag {

  static deserialize(dto: TextVraagDto): TextVraag {
    return new TextVraag(
      dto.id,
      dto.required,
      dto.label,
      dto.placeholder,
      dto.icoon
    );
  }

  constructor(id: string,
              required: boolean,
              private label: string,
              private placeholder: string,
              icoon?: string,
  ) {
    super(id, VraagType.TEXT, required, icoon);
  }

  getLabel(): string {
    return this.label;
  }

  getPlaceholder(): string {
    return this.placeholder;
  }
}

export class TextareaVraag extends Vraag {

  static deserialize(dto: TextareaVraagDto): TextareaVraag {
    return new TextareaVraag(
      dto.id,
      dto.required,
      dto.label,
      dto.placeholder,
      dto.icoon
    );
  }

  constructor(id: string,
              required: boolean,
              private label: string,
              private placeholder: string,
              icoon?: string,
  ) {
    super(id, VraagType.TEXTAREA, required, icoon);
  }

  getLabel(): string {
    return this.label;
  }

  getPlaceholder(): string {
    return this.placeholder;
  }
}

export class RadioVraag extends Vraag {

  static deserialize(dto: RadioVraagDto): RadioVraag {
    return new RadioVraag(
      dto.id,
      dto.required,
      dto.label,
      dto.options.map(optionDto => Option.deserialize(optionDto)),
      dto.icoon
    );
  }

  constructor(id: string,
              required: boolean,
              private label: string,
              private options: Array<Option>,
              icoon?: string,
  ) {
    super(id, VraagType.RADIO, required, icoon);
  }

  getLabel(): string {
    return this.isRequired() ? `${this.label} *` : this.label;
  }

  getOptions(): Array<Option> {
    return this.options;
  }
}

export class VerkeerslichtVraag extends Vraag {

  static deserialize(dto: VerkeerslichtVraagDto): VerkeerslichtVraag {
    return new VerkeerslichtVraag(dto.id, dto.required, dto.label, dto.uitleg, dto.icoon);
  }

  constructor(id: string,
              required: boolean,
              private label: string,
              private uitleg?: string,
              icoon?: string,
  ) {
    super(id, VraagType.VERKEERSLICHT, required, icoon);
  }

  getLabel(): string {
    return this.isRequired() ? `${this.label} *` : this.label;
  }

  getUitleg(): string | undefined {
    return this.uitleg;
  }

  hasUitleg(): boolean {
    return !!this.uitleg;
  }
}

export class SelectVraag extends Vraag {

  static deserialize(dto: SelectVraagDto): SelectVraag {
    return new SelectVraag(
      dto.id,
      dto.required,
      dto.label,
      dto.placeholder,
      dto.multi,
      dto.options.map(optionDto => Option.deserialize(optionDto)),
      dto.icoon
    );
  }

  constructor(id: string,
              required: boolean,
              private label: string,
              private placeholder: string,
              private multi: boolean,
              private options: Array<Option>,
              icoon?: string,
  ) {
    super(id, VraagType.SELECT, required, icoon);
  }

  getLabel(): string {
    return this.label;
  }

  getPlaceholder(): string {
    return this.placeholder;
  }

  isMulti(): boolean {
    return this.multi;
  }

  getOptions(): Array<Option> {
    return this.options;
  }

  getOption(optionId: string): Option {
    return this.options.find(option => option.hasId(optionId));
  }
}

export class Option {

  static deserialize(dto: OptionDto): Option {
    return new Option(dto.id, dto.label, dto.icoon);
  }

  constructor(private id: string,
              private label: string,
              private icoon?: string,
  ) {
  }

  getId(): string {
    return this.id;
  }

  hasId(id: string): boolean {
    return this.id === id;
  }

  getLabel(): string {
    return this.label;
  }

  getIcoon(): string | undefined {
    return this.icoon;
  }

  hasIcoon(): boolean {
    return !!this.icoon;
  }
}

export enum VraagType {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  RADIO = 'radio',
  VERKEERSLICHT = 'verkeerslicht',
  SELECT = 'select',
}

export interface OptionDto {
  id: string;
  label: string;
  icoon?: string;
}

export interface VraagDto {
  id: string;
  type: VraagType;
  required: boolean;
  icoon: string;
}

export interface TextVraagDto extends VraagDto {
  label: string;
  placeholder: string;
}

export interface TextareaVraagDto extends VraagDto {
  label: string;
  placeholder: string;
}

export interface RadioVraagDto extends VraagDto {
  label: string;
  options: Array<OptionDto>;
}

export interface VerkeerslichtVraagDto extends VraagDto {
  label: string;
  uitleg?: string;
}

export interface SelectVraagDto extends VraagDto {
  label: string;
  placeholder: string;
  multi: boolean;
  options: Array<OptionDto>;
}
